// Row 123 ReferenceCheck (`refcheck`, DC-224, DC-225): the Step 18 card on Offer > Reference Check. Its head is the
// *Requirement flag* as G1's Segmented *Not Required · Required* beside the status pill. *Not Required* collapses the
// body to his one line, "No block when not required." Otherwise G5's Rail *Required · Requested · In Progress ·
// Completed* (with *Unable to Complete* as the terminal branch: its pill replaces the last step) and the six fields
// *Owner* (G1 PersonPick) · *Provider* · *Requested* · *Completed* · *Outcome* · *Notes* (the Internal tag), the one
// primary **Record** and his hint "Subject to final policy/privacy review."
// Who sees it (BRD §3.18): the Recruiter manages; authorized HR roles view (`seat="hr"`: values only, no controls);
// the candidate and the interviewer see nothing (null, not masked). A completed check shows its values with the lock.
// States: loading draws the card at its final height; refused is the red banner with the refused rule, values kept.

import type { ReactNode } from "react";
import {
  Button, Card, EmptyState, Field, Input, KeyValue, Pill, Select, SURFACE_LABELS, Tag, TAG_LABELS, TextArea, type Blocked, type SelectOption,
} from "../core";
import { REFERENCE_STATUS, RULE_LABELS, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { formatDate } from "../lib/time";
import { PersonPick, Segmented, type PersonOption } from "../g1";
import { Rail, type RailStep } from "../g5";
import { REFCHECK_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type RefStatus = "notRequired" | "required" | "requested" | "inProgress" | "completed" | "unableToComplete";
export type RefSeat = "recruiter" | "hr" | "interviewer" | "candidate";
export type RefField = "requirement" | "owner" | "provider" | "requested" | "completed" | "outcome" | "notes" | "record";

export const REF_STATUSES: readonly RefStatus[] = ["notRequired", "required", "requested", "inProgress", "completed", "unableToComplete"];

/** The status pill of each status (DS row 123 table). */
export const REF_PILL: Readonly<Record<RefStatus, { word: string; tone: Tone }>> = {
  notRequired: { word: REFERENCE_STATUS.notRequired, tone: "neutral" },
  required: { word: REFERENCE_STATUS.required, tone: "warn" },
  requested: { word: REFERENCE_STATUS.requested, tone: "info" },
  inProgress: { word: REFERENCE_STATUS.inProgress, tone: "info" },
  completed: { word: REFERENCE_STATUS.completed, tone: "neutral" },
  unableToComplete: { word: REFERENCE_STATUS.unableToComplete, tone: "flag" },
};

export interface ReferenceCheckProps {
  status: RefStatus;
  /** Whose view (default recruiter): hr views values only; interviewer and candidate see nothing. */
  seat?: RefSeat | undefined;
  onRequirement?: ((required: boolean) => void) | undefined;
  owners: readonly PersonOption[];
  owner?: string | undefined;
  onOwner?: ((value: string) => void) | undefined;
  /** The ReferenceProvider list. */
  providers: readonly SelectOption[];
  provider?: string | undefined;
  onProvider?: ((value: string) => void) | undefined;
  outcomes: readonly SelectOption[];
  outcome?: string | undefined;
  onOutcome?: ((value: string) => void) | undefined;
  /** yyyy-mm-dd. */
  requested?: string | undefined;
  onRequested?: ((value: string) => void) | undefined;
  completed?: string | undefined;
  onCompleted?: ((value: string) => void) | undefined;
  notes?: string | undefined;
  onNotes?: ((value: string) => void) | undefined;
  onRecord?: (() => void) | undefined;
  recording?: boolean | undefined;
  /** Record is not possible yet: his rule as the description. */
  recordBlocked?: Blocked | undefined;
  /** The requirement flag cannot change (a recorded check): his rule as the description. */
  requirementBlocked?: Blocked | undefined;
  /** Validation errors per field (the required sentence). */
  errors?: Partial<Record<Exclude<RefField, "requirement" | "record">, ReactNode>> | undefined;
  loading?: boolean | undefined;
  refused?: { reason: string } | undefined;
  timeZone?: string | undefined;
  can?: boolean | undefined;
  hs?: Partial<Record<RefField, Hs>> | undefined;
  className?: string | undefined;
}

function railSteps(status: RefStatus): RailStep[] {
  const at = { notRequired: -1, required: 0, requested: 1, inProgress: 2, completed: 4, unableToComplete: 3 }[status];
  const chain = [REFERENCE_STATUS.required, REFERENCE_STATUS.requested, REFERENCE_STATUS.inProgress, REFERENCE_STATUS.completed];
  return chain.map((label, index) => ({ label, state: index < at ? "done" : index === at ? "on" : "next" }));
}

const optionLabel = (options: readonly { value: string; label?: string; name?: string }[], value: string | undefined) => {
  const found = options.find((option) => option.value === value);
  return found ? (found.label ?? found.name ?? "—") : "—";
};

export function ReferenceCheck(props: ReferenceCheckProps) {
  const {
    status, seat = "recruiter", onRequirement, owners, owner, onOwner, providers, provider, onProvider, outcomes, outcome, onOutcome, requested,
    onRequested, completed, onCompleted, notes, onNotes, onRecord, recording, recordBlocked, requirementBlocked, errors = {}, loading, refused,
    timeZone, can, hs = {}, className,
  } = props;
  if (can === false || seat === "interviewer" || seat === "candidate") return null;
  const pill = REF_PILL[status];
  const shell = {
    title: REFCHECK_LABELS.referenceCheck, icon: "shield" as const, pills: <Pill tone={pill.tone}>{pill.word}</Pill>,
    className: cx("mc-ref", className),
  };
  if (loading) return <Card {...shell} pills={undefined} loading={{ height: 360 }} />;

  const view = seat === "hr";
  const done = status === "completed" || status === "unableToComplete";
  const edit = !view && !done;
  const required = status !== "notRequired";
  const flag = (
    <Segmented
      label={REFCHECK_LABELS.requirementFlag}
      options={[
        { value: "notRequired", label: REFERENCE_STATUS.notRequired, hs: hs.requirement },
        { value: "required", label: REFERENCE_STATUS.required },
      ]}
      value={required ? "required" : "notRequired"}
      onChange={(value) => onRequirement?.(value === "required")}
      blocked={requirementBlocked}
      mode={edit ? undefined : "view"}
    />
  );
  const date = (value: string | undefined) => (value ? formatDate(`${value}T16:00:00Z`, timeZone) : "—");
  const lock = done ? "" : undefined;

  return (
    <Card {...shell} refused={refused}>
      <div className="mc-ref-head">
        <span className="mc-ref-flag-l" aria-hidden="true">{REFCHECK_LABELS.requirementFlag}</span>
        {flag}
      </div>
      {!required ? <EmptyState inline>{RULE_LABELS.noBlockWhenNotRequired}</EmptyState> : (
        <>
          <Rail
            label={REFCHECK_LABELS.referenceCheck}
            steps={railSteps(status)}
            terminal={status === "unableToComplete" ? { word: REFERENCE_STATUS.unableToComplete, tone: "flag" } : undefined}
            className="mc-ref-rail"
          />
          {edit ? (
            <div className="mc-ref-fields">
              <Field label={SURFACE_LABELS.owner} error={errors.owner}>
                <PersonPick options={owners} value={owner} onChange={onOwner} label={SURFACE_LABELS.owner} hs={hs.owner} />
              </Field>
              <Field label={SURFACE_LABELS.provider} error={errors.provider}>
                <Select options={providers} value={provider ?? ""} onChange={(value) => onProvider?.(value)} hs={hs.provider} />
              </Field>
              <Field label={REFCHECK_LABELS.requested} required={status === "requested"} error={errors.requested}>
                <Input type="date" value={requested ?? ""} onChange={(event) => onRequested?.(event.target.value)} hs={hs.requested} />
              </Field>
              <Field label={REFCHECK_LABELS.completed} error={errors.completed}>
                <Input type="date" value={completed ?? ""} onChange={(event) => onCompleted?.(event.target.value)} hs={hs.completed} />
              </Field>
              <Field label={SURFACE_LABELS.outcome} error={errors.outcome}>
                <Select options={outcomes} value={outcome ?? ""} onChange={(value) => onOutcome?.(value)} hs={hs.outcome} />
              </Field>
              <Field label={SURFACE_LABELS.notes} labelSlot={<Tag tone="internal">{TAG_LABELS.internal}</Tag>} error={errors.notes} className="mc-ref-notes">
                <TextArea rows={3} value={notes ?? ""} onChange={(event) => onNotes?.(event.target.value)} hs={hs.notes} />
              </Field>
            </div>
          ) : (
            <KeyValue
              cols={3}
              className="mc-ref-values"
              items={[
                [SURFACE_LABELS.owner, optionLabel(owners, owner), lock],
                [SURFACE_LABELS.provider, optionLabel(providers, provider), lock],
                [REFCHECK_LABELS.requested, date(requested), lock],
                [REFCHECK_LABELS.completed, date(completed), lock],
                [SURFACE_LABELS.outcome, optionLabel(outcomes, outcome), lock],
                [<span key="n" className="mc-ref-nk">{SURFACE_LABELS.notes}<Tag tone="internal">{TAG_LABELS.internal}</Tag></span>, notes || "—", lock],
              ]}
            />
          )}
        </>
      )}
      <div className="mc-ref-foot">
        <p className="mc-hint mc-ref-hint">{RULE_LABELS.policyPrivacyReview}</p>
        {edit && required ? (
          <Button tone="primary" blocked={recordBlocked} busy={recording} onClick={onRecord} hs={hs.record}>{REFCHECK_LABELS.record}</Button>
        ) : null}
      </div>
    </Card>
  );
}
